/**
 * Input — raw <input> primitive, design-token wired.
 *
 * For the full labelled-field pattern (icon + uppercase caption + error message)
 * use the Field component from ~/components/ui/field instead.
 */
import type * as React from "react";
import { cn } from "~/lib/utils";

type InputProps = React.ComponentProps<"input">;

function Input({ className, type, ...props }: InputProps) {
  return (
    <input
      type={type}
      data-slot="input"
      className={cn(
        // Layout & shape
        "flex w-full rounded-(--r-lg)",
        // Spacing
        "px-4 py-3",
        // Typography
        "font-ui text-[14px] font-medium text-(--ink)",
        // Surface — default state
        "border border-(--ink-20) bg-(--ink-05)",
        // Placeholder
        "placeholder:font-normal placeholder:text-(--ink-40)",
        // Transitions
        "[transition:border-color_var(--dur-fast)_var(--ease-snap),background-color_var(--dur-fast)_var(--ease-snap),box-shadow_var(--dur-fast)_var(--ease-snap)]",
        // Focus — blue ring
        "outline-none",
        "focus:border-(--blue) focus:bg-white focus:shadow-[0_0_0_3px_rgba(0,115,233,0.12)]",
        // Error state — applied via aria-invalid or className
        "aria-invalid:border-(--red) aria-invalid:bg-white aria-invalid:shadow-[0_0_0_3px_rgba(255,59,48,0.10)]",
        "aria-invalid:focus:border-(--red) aria-invalid:focus:shadow-[0_0_0_3px_rgba(255,59,48,0.14)]",
        // Disabled
        "disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      {...props}
    />
  );
}

export { Input };
